import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { NavbarBrand, NavLink, Navbar } from 'reactstrap';
import {
  X,
  User,
  Settings as SettingsIcon,
  Search,
  Grid,
  Users,
  MessageSquare,
} from 'react-feather';
import axiosService from '@Lib/axiosService';
import { setSideBar, changeActiveMenu } from '@Lib/slices/uiSlice';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { privateConstant } from '../../../../routers/private/contant';
import MeunuItem from './MenuItem';
import MenuItemHasChild from './MenuItemHasChild';
import MobileMenuItem from './MobileMenuItem';
import MobileMenuItemHasChild from './MobileMenuItemHasChild';
import { getCurrentSiteBranding } from '@Lib/siteBranding';
import AppSwitcher from '@Components/AppSwitcher';

import './mobile-menu.css';


function HorizontalMenu({ setMenuVisibility }) {
  const refMenu = useRef();
  const sidebar = useSelector((state) => state.ui.sidebar);
  const accessPermisions = useSelector((state) => state.user.accessPermisions);
  const sidebarActive = useSelector((state) => state.ui.sidebarActive);
  const dispatch = useDispatch();
  const history = useHistory();
  const isSettingsActive = useRouteMatch('/settings');
  const userInfo = useSelector((state) => state.user.info);
  const projectActive = useSelector((state) => state.user.projectActive);
  const [searchKey, setSearchKey] = useState('');
  const siteBranding = getCurrentSiteBranding();

  const isPrivilegedUser = React.useMemo(() => {
    if (!userInfo) return false;
    if (userInfo.is_super_admin) return true;

    const rolesByProject = userInfo.roles_by_project || {};
    const allRoles = Object.values(rolesByProject).flatMap((roles) =>
      Array.isArray(roles) ? roles : [],
    );

    return allRoles.some((role) => String(role).toLowerCase().includes('admin'));
  }, [userInfo]);

  const isSale = React.useMemo(() => {
    if (!userInfo) return false;
    if (userInfo.is_super_admin) return true;
    const projectRoles = userInfo.roles_by_project?.[projectActive?.id] || [];
    return projectRoles.some((role) => role.toLowerCase().includes('sale'));
  }, [userInfo, projectActive]);

  const handleOpenZaloOmni = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosService.get(`${process.env.URL}/sso/zalo-omni-token`);
      const { redirect_url } = response.data;
      window.open(redirect_url, '_blank');
    } catch (error) {
      console.error('Không thể mở Zalo Omni:', error);
      alert('Không thể kết nối Zalo Omni. Vui lòng thử lại.');
    }
  };

  const getAppUrl = (baseUrl, redirectPath = '/dashboard') => {
    if (!baseUrl) return '#';
    const token = localStorage.getItem(process.env.TOKEN);
    if (token) {
      return `${baseUrl}/auth/callback?token=${token}&redirect=${redirectPath}`;
    }
    return baseUrl;
  };

  // ... (keep useEffects)

  useEffect(() => {
    if (accessPermisions) {
      const sidebarConstant = privateConstant.reduce((acc, current) => {
        const { accessName, isExternal } = current;
        if (!isExternal && !isPrivilegedUser && !accessPermisions[accessName]) {
          return acc;
        }

        const menu = { ...current };
        if (menu.child) {
          menu.child = menu.child.map((itemChild) => {
            const child = { ...itemChild };
            if (!child.accessName && menu.accessName) {
              child.accessName = menu.accessName;
            }
            delete child.component;
            return child;
          });
        } else {
          delete menu.component;
        }
        acc.push(menu);
        return acc;
      }, []);
      dispatch(setSideBar(sidebarConstant));
    }
  }, [accessPermisions, isPrivilegedUser]);

  useEffect(() => {
    dispatch(changeActiveMenu(sidebarActive));
  }, [sidebarActive, accessPermisions]);

  const handleWheel = (event) => {
    if (refMenu) {
      refMenu.current.scrollLeft += event.deltaY;
    }
  };

  const handleClickItem = (item) => {
    const { path, query } = item;
    let url = path;
    if (query) {
      url = `${path}?${query}`;
    }
    history.push(url);
    if (setMenuVisibility && window.innerWidth < 768) {
      setMenuVisibility(); /* Close menu on click */
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchKey) {
      history.push(`/search?s=${searchKey}`);
      if (setMenuVisibility) setMenuVisibility();
    }
  };

  const hasNamedChildren = (item) => {
    return (
      item.child &&
      item.child.some((child) => {
        if (child.hidden) return false;
        if (!child.name) return false;
        if (
          !isPrivilegedUser &&
          child.accessName &&
          accessPermisions &&
          !accessPermisions[child.accessName]
        ) {
          return false;
        }
        return true;
      })
    );
  };

  return (
    <div className="horizontal-menu-wrapper">
      <Navbar
        tag="div"
        expand="sm"
        className="header-navbar navbar-expand-sm navbar-horizontal navbar-light menu-border d-none d-md-block floating-nav"
      >
        <div className="navbar-container main-menu-content" onWheel={handleWheel} ref={refMenu}>
          <ul className="nav navbar-nav flex-nowrap align-items-center">
            {sidebar.length > 0 &&
              sidebar.map((route) => {
                if (route.hidden) return null;
                if (route.child) {
                  return (
                    <MenuItemHasChild
                      item={route}
                      key={route.id}
                      handleClickItem={handleClickItem}
                    />
                  );
                }
                if (!route.hidden) {
                  return (
                    <MeunuItem item={route} key={route.id} handleClickItem={handleClickItem} />
                  );
                }
                return null;
              })}
            {(isPrivilegedUser || (accessPermisions && accessPermisions.setup)) && (
              <li className="nav-item">
                <NavLink
                  onClick={() => history.push('/settings')}
                  className={`${isSettingsActive ? 'active' : ''} d-flex align-items-center`}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="mr-50 d-flex align-items-center">
                    <SettingsIcon size={16} />
                  </span>
                  Cài đặt
                </NavLink>
              </li>
            )}
          </ul>
        </div>
      </Navbar>
      <div className="main-menu menu-fixed menu-accordion menu-shadow d-block d-md-none expanded menu-light">
        <div className="navbar-header expanded" style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
          <ul className="nav navbar-nav flex-row w-100 align-items-center justify-content-between" style={{ paddingRight: '1rem' }}>
            <li className="nav-item">
              <NavbarBrand className="m-0 p-0">
                <img
                  src={siteBranding.logo_url}
                  className="site-logo"
                  alt={siteBranding.name}
                  style={{ maxHeight: siteBranding.logo_max_height || undefined }}
                />
              </NavbarBrand>
            </li>
            <li className="nav-item nav-toggle" style={{ padding: 0 }}>
              <NavLink onClick={setMenuVisibility} className="p-0 d-flex align-items-center justify-content-center">
                <X color="#faa52d" size={24} />
              </NavLink>
            </li>
          </ul>
        </div>
        <div className="main-menu-content">
          <div className="mobile-search-container">
            <form onSubmit={handleSearch}>
              <div className="input-group">
                <div className="input-group-prepend">
                  <span className="input-group-text">
                    <Search size={16} color="#b9b9c3" />
                  </span>
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Tìm kiếm tên, mã, sđt khách hàng, mã đơn, thông tin sản phẩm"
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                />
              </div>
            </form>
          </div>
          <AppSwitcher mode="list" setMenuVisibility={setMenuVisibility} />
          <ul className="navigation navigation-main">

            {sidebar.length > 0 &&
              sidebar.map((route) => {
                if (route.hidden) return null;
                if (hasNamedChildren(route)) {
                  return (
                    <MobileMenuItemHasChild
                      item={route}
                      key={route.id}
                      handleClickItem={handleClickItem}
                    />
                  );
                }
                if (!route.hidden) {
                  return (
                    <MobileMenuItem item={route} key={route.id} handleClickItem={handleClickItem} />
                  );
                }
                return null;
              })}
          </ul>
        </div>
        <div className="mobile-menu-footer">
          <ul className="navigation navigation-main mb-0">
            <MobileMenuItem
              item={{ name: 'Cài đặt', path: '/settings', icon: <SettingsIcon size={18} /> }}
              handleClickItem={handleClickItem}
            />
          </ul>
        </div>
      </div>
    </div>
  );
}

HorizontalMenu.propTypes = {
  setMenuVisibility: PropTypes.func,
  sidebar: PropTypes.array,
};

export default HorizontalMenu;
